export type ThemeMode = 'light' | 'dark' | 'system';

export const ACCENT_COLORS = [
  'blue',
  'indigo',
  'violet',
  'purple',
  'fuchsia',
  'pink',
  'rose',
  'red',
  'orange',
  'amber',
  'yellow',
  'lime',
  'green',
  'emerald',
  'teal',
  'cyan',
  'sky',
  'slate',
  'gray',
  'zinc',
  'neutral',
  'stone',
  'brown',
  'copper',
  'oxblood',
  'custom',
] as const;

export type AccentColor = typeof ACCENT_COLORS[number];

export const ACCENT_COLOR_OPTIONS: { id: AccentColor; label: string; colorValue: string }[] = [
  { id: 'blue', label: 'Blue', colorValue: '#3b82f6' },
  { id: 'indigo', label: 'Indigo', colorValue: '#6366f1' },
  { id: 'violet', label: 'Violet', colorValue: '#8b5cf6' },
  { id: 'purple', label: 'Purple', colorValue: '#a855f7' },
  { id: 'fuchsia', label: 'Fuchsia', colorValue: '#d946ef' },
  { id: 'pink', label: 'Pink', colorValue: '#ec4899' },
  { id: 'rose', label: 'Rose', colorValue: '#f43f5e' },
  { id: 'red', label: 'Red', colorValue: '#ef4444' },
  { id: 'orange', label: 'Orange', colorValue: '#f97316' },
  { id: 'amber', label: 'Amber', colorValue: '#f59e0b' },
  { id: 'yellow', label: 'Yellow', colorValue: '#eab308' },
  { id: 'lime', label: 'Lime', colorValue: '#84cc16' },
  { id: 'green', label: 'Green', colorValue: '#22c55e' },
  { id: 'emerald', label: 'Emerald', colorValue: '#10b981' },
  { id: 'teal', label: 'Teal', colorValue: '#14b8a6' },
  { id: 'cyan', label: 'Cyan', colorValue: '#06b6d4' },
  { id: 'sky', label: 'Sky', colorValue: '#0ea5e9' },
  { id: 'slate', label: 'Slate', colorValue: '#64748b' },
  { id: 'gray', label: 'Gray', colorValue: '#6b7280' },
  { id: 'zinc', label: 'Zinc', colorValue: '#71717a' },
  { id: 'neutral', label: 'Neutral', colorValue: '#737373' },
  { id: 'stone', label: 'Stone', colorValue: '#78716c' },
  { id: 'brown', label: 'Brown', colorValue: '#a16207' },
  { id: 'copper', label: 'Copper', colorValue: '#c2410c' },
  { id: 'oxblood', label: 'Oxblood', colorValue: '#7E1F2C' },
  { id: 'custom', label: 'Custom', colorValue: '#3b82f6' },
];

// Surface palette — the neutral ramp that defines the app's environment
// (page/sidebar/panel/card backgrounds, borders, muted controls, neutral text).
// Persisted as `neutralBase`; applied via [data-neutral] (see lex-theme.css).
// Separate from the accent, which drives actions/emphasis only.
export const NEUTRAL_BASES = [
  'warm',
  'charcoal',
  'slate',
  'graphite',
  'midnight',
] as const;

export type NeutralBase = typeof NEUTRAL_BASES[number];

// Alias for the user-facing name; same engine, clearer intent at call sites.
export type SurfacePalette = NeutralBase;

// `swatch` = the palette's app background (dark anchor); `swatch2` = a mid stop,
// used to render a two-tone chip in the Preferences UI. Admin panel uses `swatch`.
export const NEUTRAL_BASE_OPTIONS: { id: NeutralBase; label: string; description: string; swatch: string; swatch2: string }[] = [
  { id: 'warm',     label: 'Warm',     description: 'Soft brown-black workspace.',                    swatch: '#1f1a17', swatch2: '#574b40' },
  { id: 'charcoal', label: 'Charcoal', description: 'Neutral dark background with restrained contrast.', swatch: '#161a20', swatch2: '#525c66' },
  { id: 'slate',    label: 'Slate',    description: 'Cool technical palette for dense admin work.',   swatch: '#0f172a', swatch2: '#475569' },
  { id: 'graphite', label: 'Graphite', description: 'Crisp black-gray surfaces with high legibility.', swatch: '#18181a', swatch2: '#52525b' },
  { id: 'midnight', label: 'Midnight', description: 'Deep blue-black workspace.',                     swatch: '#0a1120', swatch2: '#2d3e5d' },
];

export const DEFAULT_SURFACE_PALETTE: NeutralBase = 'warm';

// Map legacy neutral-base ids (pre-surface-palette) onto the curated set so
// stored preferences and synced admin values never resolve to a missing ramp.
const LEGACY_SURFACE_PALETTE_MAP: Record<string, NeutralBase> = {
  stone: 'warm',
  olive: 'warm',
  neutral: 'charcoal',
  mauve: 'charcoal',
  gray: 'graphite',
  zinc: 'graphite',
  // 'slate' carries over unchanged.
};

export const normalizeSurfacePalette = (value: string | null | undefined): NeutralBase => {
  if (value && (NEUTRAL_BASES as readonly string[]).includes(value)) {
    return value as NeutralBase;
  }
  if (value && LEGACY_SURFACE_PALETTE_MAP[value]) {
    return LEGACY_SURFACE_PALETTE_MAP[value];
  }
  return DEFAULT_SURFACE_PALETTE;
};

export type DensityMode = 'compact' | 'cozy' | 'comfortable';

export type ContrastMode = 'standard' | 'high';

// Vision-assistive rendering (color-vision-deficiency-safe status colors).
// Applied via [data-vision] on <html> — see lex-theme.css. 'protan-deutan'
// re-maps status semantics to a blue/orange axis (red-green safe); 'tritan'
// to a red/cyan axis (blue-yellow safe).
export type VisionMode = 'none' | 'protan-deutan' | 'tritan';

export const VISION_MODES: VisionMode[] = ['none', 'protan-deutan', 'tritan'];

export const normalizeVisionMode = (value: string | null | undefined): VisionMode =>
  value && (VISION_MODES as string[]).includes(value) ? (value as VisionMode) : 'none';

export interface AppearanceDefaultsSnapshot {
  themeMode: ThemeMode;
  accentColor: AccentColor;
  accentCustomHex?: string | null;
  neutralBase: NeutralBase;
  density: DensityMode;
  fontScale: number;
  contrastMode: ContrastMode;
  lexTheme?: string;
  visionMode?: VisionMode;
  fontCustomColor?: string | null;
  fontBodyColor?: string | null;
  fontCustomColorForce?: boolean;
  fontBodyColorForce?: boolean;
}

export interface BaseAppearancePreferences {
  themeMode: ThemeMode;
  accentColor: AccentColor;
  accentCustomHex?: string | null;
  neutralBase: NeutralBase;
  density: DensityMode;
  fontScale: number;
  contrastMode: ContrastMode;
  // Active Lex theme id from the theme registry (src/types/themeRegistry.ts),
  // or 'custom' when the user drives the individual Custom-tab controls.
  // Applying a theme is sugar over the primitives above — it writes
  // accentColor/accentCustomHex/neutralBase and tags itself here.
  lexTheme?: string;
  // Vision-assistive status-color mapping ([data-vision] on <html>).
  visionMode?: VisionMode;
  // Heading text color override (drives --lex-text-primary).
  // Kept as `fontCustomColor` for backwards-compat with v1 storage; semantically
  // this is "headings color" in the two-picker model. If only this is set and
  // fontBodyColor is null, body/tertiary/muted derive from this via CSS — same
  // behavior as v1.
  fontCustomColor?: string | null;
  // Body text color override (drives --lex-text-secondary; tertiary/muted
  // derive from it via CSS). Independent of fontCustomColor.
  fontBodyColor?: string | null;
  // Saved personal defaults. "Reset" returns here when set, else falls through
  // to the theme default. Heading + body are saved together as a pair.
  fontDefaultColor?: string | null;
  fontDefaultBodyColor?: string | null;
  // Per-picker force flags — bypass the WCAG contrast gate. Cleared on any
  // subsequent color change so each new color must be re-affirmed.
  fontCustomColorForce?: boolean;
  fontBodyColorForce?: boolean;
  // User-owned global appearance default. Reset restores this snapshot instead
  // of reverting to product or tenant defaults.
  appearanceDefaults?: AppearanceDefaultsSnapshot | null;
}

// The wallpaper id union is generated from the catalog manifest (scripts/brand/wallpapers).
import type { WallpaperId } from '@/features/edgemail-client/wallpapers/manifest';
export type { WallpaperId };
import type { WallpaperMeta } from '@/features/edgemail-client/wallpapers';
export type { WallpaperMeta };

export type PresetTheme = 'classic' | 'modern' | 'high-contrast';

export interface EdgeMailAppearancePreferences extends BaseAppearancePreferences {
  wallpaper: WallpaperId;
  // Set by the server when the user uploads an image (wallpaper === 'custom'); null otherwise.
  wallpaperCustom?: WallpaperMeta | null;
  presetTheme: PresetTheme;
  wallpaperDim: number;
  wallpaperBlur: number;
  // EdgeMail-only text overrides. These drive the --em-text-* token layer
  // without changing the system-wide --lex-text-* appearance settings.
  mailTextPrimaryColor?: string | null;
  mailTextSecondaryColor?: string | null;
  mailTextDefaultPrimaryColor?: string | null;
  mailTextDefaultSecondaryColor?: string | null;
  mailTextPrimaryColorForce?: boolean;
  mailTextSecondaryColorForce?: boolean;
  mailSidebarTextColor?: string | null;
  mailSidebarTextDefaultColor?: string | null;
  mailSidebarTextColorForce?: boolean;
}

export const createAppearanceDefaultsSnapshot = (
  preferences: BaseAppearancePreferences,
): AppearanceDefaultsSnapshot => ({
  themeMode: preferences.themeMode,
  accentColor: preferences.accentColor,
  accentCustomHex: preferences.accentCustomHex ?? null,
  neutralBase: normalizeSurfacePalette(preferences.neutralBase),
  density: preferences.density,
  fontScale: Number.isFinite(preferences.fontScale) ? preferences.fontScale : 1,
  contrastMode: preferences.contrastMode,
  lexTheme: preferences.lexTheme ?? 'custom',
  visionMode: normalizeVisionMode(preferences.visionMode),
  fontCustomColor: preferences.fontCustomColor ?? null,
  fontBodyColor: preferences.fontBodyColor ?? null,
  fontCustomColorForce: preferences.fontCustomColorForce ?? false,
  fontBodyColorForce: preferences.fontBodyColorForce ?? false,
});

export const appearanceDefaultsToUpdate = (
  defaults: AppearanceDefaultsSnapshot,
): Partial<BaseAppearancePreferences> => ({
  themeMode: defaults.themeMode,
  accentColor: defaults.accentColor,
  accentCustomHex: defaults.accentCustomHex ?? null,
  neutralBase: normalizeSurfacePalette(defaults.neutralBase),
  density: defaults.density,
  fontScale: Number.isFinite(defaults.fontScale) ? defaults.fontScale : 1,
  contrastMode: defaults.contrastMode,
  lexTheme: defaults.lexTheme ?? 'custom',
  visionMode: normalizeVisionMode(defaults.visionMode),
  fontCustomColor: defaults.fontCustomColor ?? null,
  fontBodyColor: defaults.fontBodyColor ?? null,
  fontCustomColorForce: defaults.fontCustomColorForce ?? false,
  fontBodyColorForce: defaults.fontBodyColorForce ?? false,
});

export const appearanceMatchesDefaults = (
  preferences: BaseAppearancePreferences,
  defaults: AppearanceDefaultsSnapshot | null | undefined,
): boolean => {
  if (!defaults) return false;
  const current = createAppearanceDefaultsSnapshot(preferences);
  const normalizedDefaults = createAppearanceDefaultsSnapshot({
    ...preferences,
    ...appearanceDefaultsToUpdate(defaults),
  });
  return JSON.stringify(current) === JSON.stringify(normalizedDefaults);
};
